import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import NotesIcon from "@mui/icons-material/TextSnippetRounded";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import ChangeArrow from "components/common/ChangeArrow";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import InlineSearch from "components/common/InlineSearch";
import MetricsComparison from "components/common/MetricsComparison";
import Notes from "components/common/Notes";
import Options from "components/common/Options";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { parseNumberString } from "components/common/PrettyNumberTextField.support";
import PrimaryExposure from "components/common/PrimaryExposure";
import Visible from "components/common/Visible";
import * as exposureSupport from "components/inputs/exposure/Exposure.support";
import IndustryClass from "components/inputs/program/IndustryClass";
import * as labels from "config.labels";
import { EXPOSURE_ROW_REORDER } from "flags";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDrag, useDrop } from "react-dnd";
import { connect, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as exposureActions from "store/actions/input/exposure/exposureActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import useExposureRowOrder from "store/hooks/useExposureRowOrder";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const DND_TYPE_EXPOSURE_ROW = "EXPOSURE_ROW";

const matchesSearch = (candidate, searchTerm) => {
  const lowercaseCandidate = candidate.toLowerCase();
  const lowercaseSearchTerm = searchTerm.toLowerCase();
  const modifiedSearchTerm = lowercaseSearchTerm
    .replace("_", " ")
    .replace("#", "number")
    .replace(/ave(r?a?g?e?)/, "avg")
    .replace("semis", "semi's")
    .replace(/centr(e?s?)/, "centers");
  return lowercaseCandidate.includes(modifiedSearchTerm);
};

const MetricsSelector = ({ list, exposureData, onClick }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [exposureFilter, setExposureFilter] = useState("");
  const searchOptions = (option) => {
    return matchesSearch(option.label, exposureFilter);
  };
  const flattenedData = Object.values(exposureData).reduce((acc, dict) => {
    return {
      ...acc,
      ...dict,
    };
  }, {});

  const options = list.map((metric) => {
    return {
      id: metric.key,
      label: metric.name,
      selected: metric.selected,
      faded: flattenedData[metric.key] != null,
    };
  });

  return (
    <Component
      lg_width={6}
      title={translateLabel(labels.EXPOSURE_TRACKED_METRICS_TITLE, {
        default: "Tracked Exposure Metrics",
      })}
      subtitle={translateLabel(labels.EXPOSURE_TRACKED_METRICS_SUBTITLE, {
        default:
          "Select which metrics to track for this insured. Metrics shared across this industry class cannot be unselected.",
      })}
    >
      <InlineSearch
        placeholder={"Quick Search"}
        onChange={(searchText) => setExposureFilter(searchText)}
        sx={{ marginBottom: "2rem" }}
        enableTrimming={false}
        disabled={true}
      />

      <Options
        items={options.filter(searchOptions)}
        onClick={({ id }) => {
          const item = list.find((x) => x.key === id);
          onClick(item);
        }}
      />
    </Component>
  );
};

const NotesCell = ({ rowKey, comment, updateComment }) => {
  const [editMode, setEditMode] = useState(false);
  return (
    <TableCell
      colSpan={7}
      className={"notes-column " + (editMode ? "edit-mode" : "read-mode")}
    >
      <Notes
        title={null}
        update={(comment) =>
          updateComment({
            key: rowKey,
            value: comment,
          })
        }
        onEnterEditMode={() => {
          setEditMode(true);
        }}
        onExitEditMode={() => {
          setEditMode(false);
        }}
        value={comment ?? ""}
        hasEditButton={false}
      />
    </TableCell>
  );
};

const ExposureCell = memo(
  ({ rowKey, year, currentValue, priorValue, updateValue }) => {
    const inputRef = useRef();

    const allowPasteExposureRow = staticDataService.useFeatureFlag(
      "ui/pasteExposureRow",
      { default: true }
    );

    const onChangeNumber = useCallback(
      (x) => {
        updateValue(year, rowKey, x);
      },
      [rowKey, year, updateValue]
    );

    const onPaste = useCallback(
      (e) => {
        if (!allowPasteExposureRow) {
          return;
        }

        const text = e?.clipboardData?.getData("Text");
        const items = exposureSupport.tryParse1dArray(text);
        if (items?.length > 0) {
          items.forEach((item, i) => {
            const value = parseNumberString(item);
            updateValue(year - i, rowKey, isFinite(value) ? value : null);
          });
          e.preventDefault();
          inputRef.current?.blur?.();
          return;
        }
      },
      [rowKey, year, updateValue, allowPasteExposureRow, inputRef]
    );

    return (
      <>
        <TableCell>
          <PrettyNumberTextField
            type={"text"}
            id={`${rowKey}-${year}-pntf`}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{ style: { textAlign: "right" } }}
            hiddenLabel
            size={"small"}
            variant={"filled"}
            onChangeNumber={onChangeNumber}
            onPaste={onPaste}
            value={currentValue}
            inputRef={inputRef}
            fullWidth
          />
        </TableCell>
        <TableCell className={"arrow-column"}>
          <ChangeArrow currentValue={currentValue} priorValue={priorValue} />
        </TableCell>
      </>
    );
  }
);

const ExposureRow = memo(
  ({
    row,
    rowData,
    comment,
    years,
    customKeys,
    updateValue,
    updateComment,
    toggleExposureMetric,
    isShowingComment,
    hideExposureComment,
    showExposureComment,
    isExposureFromTemplate,
    index,
    onHover,
    onDragStart,
    onDrop,
    isReorderEnabled,
  }) => {
    const theme = useTheme();
    const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
      () => ({
        type: DND_TYPE_EXPOSURE_ROW,
        item: () => {
          onDragStart?.(index);
          return { index, key: row.key };
        },
        end: () => {
          onDrop?.();
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [index, row.key, onDragStart, onDrop]
    );

    const [{ isOver }, dropRef] = useDrop(
      () => ({
        accept: DND_TYPE_EXPOSURE_ROW,
        hover: (item, monitor) => {
          if (!monitor.isOver({ shallow: true })) return;
          if (item.index === index) return;
          onHover?.(item.index, index);
          item.index = index;
        },
        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
        }),
      }),
      [onHover, index]
    );

    return (
      <Fragment key={row.key}>
        <TableRow
          ref={(el) => {
            if (isReorderEnabled && el) {
              dragPreviewRef(el);
            }
          }}
          sx={{
            opacity: isDragging ? 0.4 : 1,
            backgroundColor: isOver ? theme.brand.accent[300] : null,
            transition: "150ms",
          }}
        >
          {isReorderEnabled && (
            <TableCell
              className={"drag-column"}
              sx={{
                minWidth: "2.5rem !important",
                textAlign: "center",
                border: "none",
                cursor: isDragging ? "grabbing" : "grab",
              }}
              ref={(el) => {
                if (el) {
                  dragRef(el);
                  dropRef(el);
                }
              }}
            >
              <DragIndicatorIcon
                sx={{
                  fontSize: 20,
                  verticalAlign: "middle",
                  color: "#757575",
                }}
              />
            </TableCell>
          )}
          <TableCell className={"eye-column"}>
            {customKeys.filter(
              (key) => key.name === row.name && key.units === row.units
            ).length === 0 &&
              row.key !== "REVENUE" && (
                <IconButton
                  icon={VisibilityOffIcon}
                  onClick={() => {
                    toggleExposureMetric({
                      ...row,
                      fromTemplate: isExposureFromTemplate,
                      selected: true,
                    });
                  }}
                />
              )}
          </TableCell>
          <TableCell className={"name-column"} scope={"row"}>
            <Grid container alignItems={"center"}>
              <Typography variant={"subtitle1"}>{row.name}</Typography>
              {!!comment && (
                <IconButton
                  variant={"bright"}
                  scale={"small"}
                  icon={NotesIcon}
                  onClick={
                    isShowingComment(row.key)
                      ? () =>
                          hideExposureComment({
                            key: row.key,
                          })
                      : () =>
                          showExposureComment({
                            key: row.key,
                          })
                  }
                />
              )}
            </Grid>
            {row.key === "REVENUE" ? (
              <Visible byTag={"input.exposure.revenueRequired"}>
                <div>
                  <span
                    style={{
                      background: "#FFCFCF",
                      color: theme.brand.red[800],
                      display: "inline-block",
                      letterSpacing: "0.2px",
                      fontSize: "11px",
                      padding: "1px 4px",
                      borderRadius: "4px",
                    }}
                  >
                    {"Required"}
                  </span>
                </div>
              </Visible>
            ) : (
              ""
            )}
          </TableCell>
          <TableCell className={"units-column"} align={"right"}>
            <Typography variant={"subtitle2"}>{row.units}</Typography>
          </TableCell>

          {years.map((year) => (
            <ExposureCell
              key={year}
              year={year}
              rowKey={row.key}
              currentValue={rowData?.[year] ?? null}
              priorValue={rowData?.[year - 1] ?? null}
              updateValue={updateValue}
            />
          ))}
        </TableRow>
        {isShowingComment(row.key) && (
          <TableRow>
            {isReorderEnabled && <TableCell className={"drag-column"} />}
            <TableCell className={"eye-column"} />
            <TableCell className={"name-column"} />
            <TableCell className={"units-column"} />
            <NotesCell
              rowKey={row.key}
              comment={comment}
              updateComment={updateComment}
            />
          </TableRow>
        )}
      </Fragment>
    );
  }
);

const CustomMetrics = (props) => {
  const [shouldFocusNew, setShouldFocusNew] = useState(false);
  const newMetricInput = useRef(null);
  useEffect(() => {
    if (shouldFocusNew) {
      setShouldFocusNew(false);
      setTimeout(() => newMetricInput.current?.focus(), 10);
    }
  }, [shouldFocusNew]);

  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const makeTextKey = useCallback((item) => {
    const normalise = (value) =>
      typeof value === "string" ? value.trim().toLowerCase() : "";
    return `${normalise(item?.name)}::${normalise(item?.units)}`;
  }, []);

  const priorByTextKey = useMemo(() => {
    const map = new Map();
    (props.priorCustomKeys ?? [])
      .filter((item) => !item.deleted)
      .forEach((item) => {
        const key = makeTextKey(item);
        if (!key || key === "::") {
          return;
        }
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(item);
      });
    return map;
  }, [props.priorCustomKeys, makeTextKey]);

  const mapping = useMemo(() => {
    const entries = {};
    const currentKeys = new Set(
      (props.customKeys ?? [])
        .filter((item) => !item.newExposure && !item.deleted)
        .map((item) => item.key)
    );
    const usedTargetKeys = new Set();

    (props.customKeys ?? [])
      .filter((item) => !item.newExposure && !item.deleted)
      .forEach((item) => {
        const textKey = makeTextKey(item);
        const candidates = priorByTextKey.get(textKey);
        if (candidates?.length === 1) {
          const candidate = candidates[0];
          if (
            candidate.key !== item.key &&
            !currentKeys.has(candidate.key) &&
            !usedTargetKeys.has(candidate.key)
          ) {
            entries[item.key] = candidate.key;
            usedTargetKeys.add(candidate.key);
          }
        }
      });
    return entries;
  }, [props.customKeys, priorByTextKey, makeTextKey]);

  const hasMapping = Object.keys(mapping).length > 0;

  return (
    <Component
      lg_width={6}
      title={translateLabel(labels.CUSTOM_METRICS_TITLE, {
        default: "Custom Exposure Metrics",
      })}
      subtitle={translateLabel(labels.CUSTOM_METRICS_SUBTITLE, {
        default:
          "Add any exposure metrics you would like to track for this risk only.",
      })}
      options={
        <Button
          disabled={!hasMapping}
          onClick={() => props.remapCustomMeasureKeys(mapping)}
          variant={"contained"}
          color={"primary"}
          sx={{ mt: "10px" }}
        >
          {translateLabel(labels.CUSTOM_METRICS_REMAP_PRIOR_BUTTON, {
            default: "Link Prior Submission Metrics",
          })}
        </Button>
      }
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>
                {translateLabel(labels.CUSTOM_METRICS_MEASURE_COLUMN, {
                  default: "Measure",
                })}
              </TableCell>
              <TableCell>
                {translateLabel(labels.CUSTOM_METRICS_UNIT_COLUMN, {
                  default: "Unit",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.customKeys
              .filter((item) => !item.deleted)
              .map((item) => {
                return (
                  <TableRow key={`CUSTOM_KEY_ROW_${item.key}`}>
                    <TableCell>
                      {!item.newExposure && (
                        <IconButton
                          scale={"small"}
                          icon={DeleteIcon}
                          onClick={() => {
                            props.deleteCustomMeasure(item.key);
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type={"text"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        hiddenLabel
                        autoComplete={"off"}
                        size={"small"}
                        variant={"filled"}
                        inputRef={item.newExposure ? newMetricInput : null}
                        onChange={(e) => {
                          props.updateCustomMeasureName(
                            item.key,
                            e.target.value
                          );
                          if (
                            e.target.value === "" &&
                            (item.units ?? "") === ""
                          ) {
                            setShouldFocusNew(true);
                          }
                        }}
                        value={item.name ?? ""}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type={"text"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        hiddenLabel
                        autoComplete={"off"}
                        size={"small"}
                        variant={"filled"}
                        onChange={(e) =>
                          props.updateCustomMeasureUnit(
                            item.key,
                            e.target.value
                          )
                        }
                        value={item.units ?? ""}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Component>
  );
};

export const ExposureComponent = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [isChartsOpen, setIsChartsOpen] = useState(false);
  const trendData = exposureSupport.getExposureTrendData(
    props.rows,
    props.data
  );

  const componentVisibility =
    useSelector(userSelectors.selectUserConfig)?.componentVisibility ?? {};
  const isReorderEnabled = componentVisibility[EXPOSURE_ROW_REORDER] ?? false;

  const { rows } = props;
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);
  const {
    getRowOrderForSubmission,
    setRowOrderForSubmission,
  } = useExposureRowOrder();

  const localRowOrderRef = useRef(null);
  const [, forceUpdate] = useState(0);
  const dragStartIndexRef = useRef(null);
  const isDraggingRef = useRef(false);

  const userPreferredOrder = submissionId
    ? getRowOrderForSubmission(submissionId)
    : null;

  const displayRows = useMemo(() => {
    const currentOrder = localRowOrderRef.current;
    if (currentOrder) {
      const rowMap = new Map(rows.map((row) => [row.key, row]));
      return currentOrder.map((key) => rowMap.get(key)).filter(Boolean);
    }
    if (userPreferredOrder && userPreferredOrder.length > 0) {
      const rowMap = new Map(rows.map((row) => [row.key, row]));
      const orderedRows = userPreferredOrder
        .map((key) => rowMap.get(key))
        .filter(Boolean);
      const remainingRows = rows.filter(
        (row) => !userPreferredOrder.includes(row.key)
      );
      return [...orderedRows, ...remainingRows];
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, localRowOrderRef.current, userPreferredOrder]);

  const handleDragStart = useCallback(
    (index) => {
      isDraggingRef.current = true;
      dragStartIndexRef.current = index;
      localRowOrderRef.current = rows.map((row) => row.key);
      forceUpdate((n) => n + 1);
    },
    [rows]
  );

  const handleHover = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    if (!isDraggingRef.current) return;
    const prevOrder = localRowOrderRef.current;
    if (!prevOrder) return;
    const newOrder = [...prevOrder];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    localRowOrderRef.current = newOrder;
    forceUpdate((n) => n + 1);
  }, []);

  const handleDrop = useCallback(() => {
    const localRowOrder = localRowOrderRef.current;
    if (localRowOrder && dragStartIndexRef.current !== null && submissionId) {
      const originalKeys = rows.map((row) => row.key);
      const draggedKey = originalKeys[dragStartIndexRef.current];
      const finalIndex = localRowOrder.indexOf(draggedKey);

      if (finalIndex !== dragStartIndexRef.current) {
        setRowOrderForSubmission(submissionId, localRowOrder);
      }
    }
    isDraggingRef.current = false;
    dragStartIndexRef.current = null;
    localRowOrderRef.current = null;
    forceUpdate((n) => n + 1);
  }, [rows, submissionId, setRowOrderForSubmission]);

  const exposureKeyToFromTemplateMapping = useMemo(
    () =>
      Object.fromEntries(
        (props.toggleList ?? []).map((defn) => [defn.key, defn.fromTemplate])
      ),
    [props.toggleList]
  );

  return (
    <>
      {isChartsOpen && (
        <MetricsComparison
          open={true}
          trendData={trendData}
          chartData={exposureSupport.getExposureChartData(props.data)}
          onClose={() => setIsChartsOpen(false)}
        />
      )}
      <Component
        title={translateLabel(labels.HISTORICAL_EXPOSURE_TITLE, {
          default: "Historical Exposure",
        })}
        subtitle={
          <Visible byTag={"input.exposure.revenueRequired"}>
            {translateLabel(labels.REVENUE_REQUIRED_SUBTITLE, {
              default:
                "Revenue is required to calculate the Model average loss frequency. The Insured average loss frequency can be calculated using any of the exposure metrics.",
            })}
          </Visible>
        }
      >
        {props.rowsUpdating ? (
          <DefaultLoader
            style={{
              width: "200px",
              height: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            size={60}
          />
        ) : props.industryClass ? (
          <>
            <Stack
              direction={"row"}
              spacing={1}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              <Button
                size={"small"}
                variant={"outlined"}
                onClick={() => {
                  props.addExposureYear(Math.min(...props.years));
                }}
                sx={{ color: "primary.main" }}
              >
                {"Add Year"}
              </Button>
              <Button
                size={"small"}
                variant={"outlined"}
                onClick={
                  props.commentVisibility.defaultVisible
                    ? props.hideAllExposureComments
                    : props.showAllExposureComments
                }
                sx={{ color: "primary.main" }}
              >
                {props.commentVisibility.defaultVisible
                  ? "Hide Notes"
                  : "Show Notes"}
              </Button>
              <span>
                <IconButton
                  variant={"bright"}
                  icon={QueryStatsRoundedIcon}
                  tooltip={translateLabel(labels.HISTORICAL_EXPOSURE_TOOLTIP, {
                    default: "Compare Exposures",
                  })}
                  onClick={() => setIsChartsOpen(true)}
                  isDisabled={trendData.length === 0}
                />
              </span>
            </Stack>
            <TableContainer sx={{ maxHeight: 800 }}>
              <Table
                className={"exposure-inputs"}
                size={"small"}
                stickyHeader={true}
              >
                <TableHead>
                  <TableRow>
                    {isReorderEnabled && <TableCell sx={{ border: "none" }} />}
                    <TableCell className={"eye-header"} />
                    <TableCell className={"name-header"}>
                      {translateLabel(
                        labels.HISTORICAL_EXPOSURE_MEASURE_COLUMN,
                        {
                          default: "Measure",
                        }
                      )}
                    </TableCell>
                    <TableCell align={"right"} className={"unit-header"}>
                      {translateLabel(labels.HISTORICAL_EXPOSURE_UNIT_COLUMN, {
                        default: "Unit",
                      })}
                    </TableCell>
                    {props.years.map((year) => (
                      <Fragment key={`EXPOSURE_ARROW_HEADER_${year}`}>
                        <TableCell className={"years-header"} align={"right"}>
                          {(props.labels[year] ?? []).includes("projected")
                            ? translateLabel(
                                labels.HISTORICAL_EXPOSURE_PROJECTED_LABEL,
                                {
                                  default: "Projected",
                                }
                              )
                            : year.toString()}
                        </TableCell>
                        <TableCell className={"years-header"}></TableCell>
                      </Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.map((row, index) => (
                    <ExposureRow
                      key={`${row.key}-${index}`}
                      row={row}
                      index={index}
                      rowData={Object.fromEntries(
                        Object.entries(props.data).map(([k, v]) => [
                          k,
                          v[row.key],
                        ])
                      )}
                      comment={props.comments?.[row.key]}
                      years={props.years}
                      customKeys={props.customKeys}
                      toggleExposureMetric={props.toggleExposureMetric}
                      isShowingComment={props.isShowingComment}
                      hideExposureComment={props.hideExposureComment}
                      showExposureComment={props.showExposureComment}
                      updateValue={props.updateValue}
                      updateComment={props.updateComment}
                      isExposureFromTemplate={
                        exposureKeyToFromTemplateMapping[row.key] ?? false
                      }
                      onDragStart={handleDragStart}
                      onHover={handleHover}
                      onDrop={handleDrop}
                      isReorderEnabled={isReorderEnabled}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography>
            {translateLabel(labels.SELECT_INDUSTRY_CLASS_MESSAGE, {
              default: "Please select an industry class.",
            })}
          </Typography>
        )}
      </Component>
      <Grid item xs={12} lg={6}>
        <IndustryClass componentProps={{ growVertically: true }} />
      </Grid>
      <PrimaryExposure hideChart md_width={12} lg_width={6} />
      <MetricsSelector
        list={props.toggleList}
        exposureData={props.data}
        onClick={props.toggleExposureMetric}
      />
      <CustomMetrics {...props} />
      <Notes
        md_width={12}
        lg_width={6}
        update={props.updateNote}
        value={props.note}
      />
    </>
  );
};

const mapDispatchToProps = {
  updateValue: exposureActions.updateValue,
  updateNote: exposureActions.updateExposureNote,
  updateComment: exposureActions.updateExposureComment,
  showAllExposureComments: exposureActions.showAllExposureComments,
  hideAllExposureComments: exposureActions.hideAllExposureComments,
  showExposureComment: exposureActions.showExposureComment,
  hideExposureComment: exposureActions.hideExposureComment,
  toggleExposureMetric: exposureActions.toggleExposureMetric,
  updateCustomMeasureUnit: exposureActions.updateCustomMeasureUnit,
  updateCustomMeasureName: exposureActions.updateCustomMeasureName,
  deleteCustomMeasure: exposureActions.deleteCustomMeasure,
  remapCustomMeasureKeys: exposureActions.remapCustomMeasureKeys,
  addExposureYear: exposureActions.addExposureYear,
};

const mapStateToProps = (state) => {
  return {
    inception: state?.input?.program?.inception ?? null,
    rows: exposureSelectors.getExposureList(state),
    data: exposureSelectors.getData(state),
    rowsUpdating: exposureSelectors.isExposureListUpdating(state),
    toggleList: exposureSelectors.getExposureToggleList(state),
    customKeys: exposureSelectors.getCustomKeysToDisplay(state),
    priorCustomKeys: exposureSelectors.getCustomKeys(
      stateSelectors.selectPriorState(state)
    ),
    industryClass: state?.input?.program?.industryClass ?? null,
    note: state?.input?.exposure?.note || "",
    comments: state?.input?.exposure?.comments ?? {},
    commentVisibility: state?.input?.exposure?.commentVisibility ?? {},
    years: exposureSelectors.getExposuresYearsToDisplay(state),
    labels: exposureSelectors.getExposuresLabels(state),
    isShowingComment: exposureSelectors.selectIsShowingCommentFunction(state),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ExposureComponent);
